import { useState, useEffect, useCallback } from "react";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import { useApi } from "../hooks/useApi";
import {
  Box,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddAccountButton from "../components/common/AddAccountButton";
import PaymentProcessorsTable from "../components/common/PaymentProcessorsTable";
import BrandsManager from "../components/common/BrandsManager";
import StripeDialog from "../components/common/StripeDialog";
import PayPalDialog from "../components/common/PayPalDialog";
import AdyenDialog from "../components/common/AdyenDialog";
import AdyenMerchantBrandsDialog from "../components/common/AdyenMerchantBrandsDialog";
import { PPAccountIntegration } from "../types/paymentProcessor";
import { useBrands } from "../hooks/useBrands";
import { DownloadDisputesResponse } from "../types/downloadDisputesResponse";
import { AdyenDisputeImportResult } from "../types/adyenDisputeImport";
import { formatAdyenImportResult } from "../utils/formatAdyenImportResult";
import { AdyenPaymentImportResult } from "../types/adyenPaymentImport";
import { formatAdyenPaymentImportResult } from "../utils/formatAdyenPaymentImportResult";
import { AdyenPaymentDayCount, AdyenPaymentDaysResponse } from "../types/adyenPaymentDays";
import { SHOPIFY_APP_STORE_URL } from "../constants/shopify";

export default function Integrations() {
  const { get, post, postForm, patch, loading, error } = useApi();
  const [activeTab, setActiveTab] = useState<'accounts' | 'brands'>('accounts');
  const [brandCreateRequested, setBrandCreateRequested] = useState(false);
  const [ppAccounts, setPpAccounts] = useState<PPAccountIntegration[]>([]);
  const [openStripeDialog, setOpenStripeDialog] = useState(false);
  const [openPayPalDialog, setOpenPayPalDialog] = useState(false);
  const [openAdyenDialog, setOpenAdyenDialog] = useState(false);
  const [mappingAccount, setMappingAccount] = useState<{ accountId: number; name: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [downloadingAccountId, setDownloadingAccountId] = useState<number | null>(null);
  const { brands, refreshBrands } = useBrands();

  const openShopifyAppStore = () => {
    window.open(SHOPIFY_APP_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const fetchPpAccounts = useCallback(async () => {
    try {
      const response = await get<PPAccountIntegration[]>(`/api/pp-accounts/integrations`);
      setPpAccounts(response);
    } catch (err) {
      console.error('Failed to fetch payment processors:', err);
    }
  }, [get]);

  useEffect(() => {
    fetchPpAccounts();
  }, [fetchPpAccounts]);

  const handleStripeDownloadDisputes = async (accountId: number) => {
    setDownloadingAccountId(accountId);
    try {
      const response = await post<DownloadDisputesResponse>(`/api/pp-accounts/${accountId}/download-stripe-disputes`, {}, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Download completed (${response.newDisputesCount} new disputes, ${response.updatedDisputesCount ?? 0} updated disputes)`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download disputes',
        severity: 'error',
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handleShopifyDownloadDisputes = async (accountId: number) => {
    setDownloadingAccountId(accountId);
    try {
      const response = await post<DownloadDisputesResponse>(`/api/pp-accounts/${accountId}/download-shopify-disputes`, {}, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Download completed (${response.newDisputesCount} new disputes, ${response.updatedDisputesCount ?? 0} updated disputes)`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download disputes',
        severity: 'error',
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handlePayPalDownloadDisputes = async (accountId: number) => {
    setDownloadingAccountId(accountId);
    try {
      const response = await post<DownloadDisputesResponse>(`/api/pp-accounts/${accountId}/download-paypal-disputes`, {}, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Download completed (${response.newDisputesCount} new disputes, ${response.updatedDisputesCount ?? 0} updated disputes)`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download disputes',
        severity: 'error',
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handleAdyenImportDisputes = async (accountId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await postForm<AdyenDisputeImportResult>(
        `/api/pp-accounts/${accountId}/import-adyen-disputes`,
        formData,
        { slowApi: true },
      );
      setSnackbar({ open: true, message: formatAdyenImportResult(result), severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to import dispute report', severity: 'error' });
      throw err;
    }
  };

  const handleAdyenPaymentDays = async (accountId: number): Promise<AdyenPaymentDayCount[]> => {
    const result = await get<AdyenPaymentDaysResponse>(`/api/pp-accounts/${accountId}/adyen-payment-days`);
    return result.days;
  };

  const handleAdyenImportPayments = async (accountId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await postForm<AdyenPaymentImportResult>(
        `/api/pp-accounts/${accountId}/import-adyen-payments`,
        formData,
        { slowApi: true },
      );
      setSnackbar({ open: true, message: formatAdyenPaymentImportResult(result), severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to import transaction report', severity: 'error' });
      throw err;
    }
  };

  const handleBrandChange = async (ppType: string, accountId: number, brandId: number) => {
    try {
      await patch(`/api/pp-accounts/${ppType}/${accountId}`, { brandId });
      await fetchPpAccounts();
    } catch {
      // error is set by useApi
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Integrations"
        description="Chargemont | Integrations"
      />
      <PageContainer>
        <PageHeader title="Integrations" />
        <div className="mb-4 flex items-center justify-between">
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(_, newTab) => {
              if (newTab !== null) setActiveTab(newTab);
            }}
            size="small"
            sx={{
              height: 40,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 2.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value="accounts">Payment Processors</ToggleButton>
            <ToggleButton value="brands">Brands</ToggleButton>
          </ToggleButtonGroup>
          {activeTab === 'accounts' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <AddAccountButton label="+ Stripe" onClick={() => setOpenStripeDialog(true)} />
              <AddAccountButton label="+ PayPal" onClick={() => setOpenPayPalDialog(true)} />
              <AddAccountButton label="+ Adyen" onClick={() => setOpenAdyenDialog(true)} />
              <AddAccountButton
                label="+ Install Chargemont Shopify App"
                onClick={openShopifyAppStore}
              />
            </Box>
          )}
          {activeTab === 'brands' && (
            <AddAccountButton label="+ Brand" onClick={() => setBrandCreateRequested(true)} />
          )}
        </div>

        {activeTab === 'accounts' && (
        <div className="mb-8">

          {/* Error State */}
          {error && (
            <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              Error: {error}
            </Box>
          )}

          {/* Payment Processors Table */}
          <PaymentProcessorsTable
            accounts={ppAccounts}
            loading={loading}
            downloadingAccountId={downloadingAccountId}
            handleStripeDownloadDisputes={handleStripeDownloadDisputes}
            handleShopifyDownloadDisputes={handleShopifyDownloadDisputes}
            handlePayPalDownloadDisputes={handlePayPalDownloadDisputes}
            brands={brands}
            onBrandChange={handleBrandChange}
            onAdyenMapMerchants={(accountId, name) => setMappingAccount({ accountId, name })}
            onShopifyAppInstall={openShopifyAppStore}
            onAdyenImportDisputes={handleAdyenImportDisputes}
            onAdyenImportPayments={handleAdyenImportPayments}
            onAdyenPaymentDays={handleAdyenPaymentDays}
          />
        </div>
        )}

        {activeTab === 'brands' && (
          <BrandsManager
            brands={brands}
            onBrandsChange={() => { refreshBrands(); fetchPpAccounts(); }}
            createRequested={brandCreateRequested}
            onCreateHandled={() => setBrandCreateRequested(false)}
          />
        )}

        {/* Link Stripe Dialog */}
        <StripeDialog
          open={openStripeDialog}
          onClose={() => setOpenStripeDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl="/api/pp-accounts/stripe"
          brands={brands}
          refreshBrands={refreshBrands}
        />

        {/* Link PayPal Dialog */}
        <PayPalDialog
          open={openPayPalDialog}
          onClose={() => setOpenPayPalDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl="/api/pp-accounts/paypal"
          brands={brands}
          refreshBrands={refreshBrands}
        />

        <AdyenDialog
          open={openAdyenDialog}
          onClose={() => setOpenAdyenDialog(false)}
          onSuccess={async (created) => {
            await fetchPpAccounts();
            if (created.mappingRequired) {
              setMappingAccount({ accountId: created.adyenAccountId, name: created.name });
            }
          }}
          apiUrl="/api/pp-accounts/adyen"
          environments={['live']}
        />

        <AdyenMerchantBrandsDialog
          open={mappingAccount !== null}
          accountId={mappingAccount?.accountId ?? null}
          accountName={mappingAccount?.name}
          apiBase="/api/pp-accounts/adyen"
          brands={brands}
          refreshBrands={refreshBrands}
          onClose={() => setMappingAccount(null)}
          onSaved={fetchPpAccounts}
        />

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PageContainer>
    </div>
  );
}
